// Edge-Function backend adapter — implements BackendPort over HTTPS.
//
// Talks to the three Supabase Edge Functions:
//   POST /functions/v1/ai-generate     (Bearer user JWT)  -> result + balance
//   POST /functions/v1/ai-generate     (op:'__balance__') -> NOT used; balance
//        is read from generate responses + a dedicated current-balance path.
//   GET  /functions/v1/founder-status  (public)           -> founder counter
//
// The server is authoritative for cost/credits; this adapter only marshals
// requests and maps HTTP status -> typed BackendError (402 -> insufficient_credits).

import type {
  BackendPort,
  GenerateRequest,
  GenerateResponse,
  FounderStatus,
} from '../bridge/ports';
import { BackendError } from '../bridge/ports';

export interface EdgeBackendOptions {
  /** Supabase project URL, e.g. https://abcd.supabase.co */
  supabaseUrl: string;
  /** Public anon key, sent as apikey on every request. */
  anonKey: string;
  /** Injectable for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /** Per-attempt timeout in ms (AbortController). Default 30s. */
  timeoutMs?: number;
  /** Max RETRIES on transient failures (total attempts = maxRetries + 1). Default 2. */
  maxRetries?: number;
  /** Base backoff in ms; grows exponentially with jitter. Default 300. */
  retryBaseMs?: number;
  /** Injectable delay (tests pass an instant resolver to avoid real waits). */
  delayImpl?: (ms: number) => Promise<void>;
}

interface GenerateErrorBody {
  error?: string;
}

const defaultDelay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * Transient HTTP statuses safe to retry. Deliberately NARROW: only rate-limit
 * (429) and service-unavailable (503). Application errors like 402
 * (insufficient_credits) or 502 (generation_failed, which already refunded
 * server-side) must NOT be retried.
 */
function isTransientStatus(status: number): boolean {
  return status === 429 || status === 503;
}

export class EdgeBackend implements BackendPort {
  private readonly base: string;
  private readonly anonKey: string;
  private readonly fetchImpl: typeof fetch;
  private readonly timeoutMs: number;
  private readonly maxRetries: number;
  private readonly retryBaseMs: number;
  private readonly delay: (ms: number) => Promise<void>;

  constructor(opts: EdgeBackendOptions) {
    // Normalise: strip trailing slash, append the functions path.
    this.base = opts.supabaseUrl.replace(/\/+$/, '') + '/functions/v1';
    this.anonKey = opts.anonKey;
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.timeoutMs = opts.timeoutMs ?? 30_000;
    this.maxRetries = opts.maxRetries ?? 2;
    this.retryBaseMs = opts.retryBaseMs ?? 300;
    this.delay = opts.delayImpl ?? defaultDelay;
  }

  /**
   * fetch wrapper adding a per-attempt timeout and bounded exponential-backoff
   * retry for transient failures (network errors, aborts, 429, 503). Retries are
   * safe because the server is idempotent on requestId.
   */
  private async fetchResilient(url: string, init: RequestInit): Promise<Response> {
    let lastErr: unknown;
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), this.timeoutMs);
      try {
        const res = await this.fetchImpl(url, { ...init, signal: ctrl.signal });
        if (isTransientStatus(res.status) && attempt < this.maxRetries) {
          await this.backoff(attempt);
          continue;
        }
        return res;
      } catch (err) {
        lastErr = err;
        if (attempt < this.maxRetries) {
          await this.backoff(attempt);
          continue;
        }
      } finally {
        clearTimeout(timer);
      }
    }
    throw new BackendError('network_error', 0, { cause: lastErr });
  }

  private backoff(attempt: number): Promise<void> {
    const expo = this.retryBaseMs * 2 ** attempt;
    const jitter = expo * 0.25 * Math.random();
    return this.delay(expo + jitter);
  }

  async generate(req: GenerateRequest, accessToken: string): Promise<GenerateResponse> {
    const res = await this.fetchResilient(`${this.base}/ai-generate`, {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({
        op: req.op,
        requestId: req.requestId,
        ...(req.imageDataUrl !== undefined ? { imageDataUrl: req.imageDataUrl } : {}),
        ...(req.prompt !== undefined ? { prompt: req.prompt } : {}),
        ...(req.quality !== undefined ? { quality: req.quality } : {}),
      }),
    });

    if (!res.ok) throw await this.toError(res);

    const body = (await res.json()) as GenerateResponse;
    return body;
  }

  async topUp(accessToken: string, requestId: string): Promise<number> {
    // Mock "Stripe" top-up: the dev-topup function grants a fixed credit pack to
    // a non-anonymous caller and returns the new authoritative balance.
    const res = await this.fetchResilient(`${this.base}/dev-topup`, {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({ requestId }),
    });
    if (!res.ok) throw await this.toError(res);
    const body = (await res.json()) as { balance?: number };
    return typeof body.balance === 'number' ? body.balance : 0;
  }

  async getBalance(accessToken: string): Promise<number> {
    // ai-generate with a zero-cost op ('idea') returns the current balance
    // without charging, giving us an authoritative read using the same auth path.
    const res = await this.fetchResilient(`${this.base}/ai-generate`, {
      method: 'POST',
      headers: this.authHeaders(accessToken),
      body: JSON.stringify({ op: 'idea', requestId: `balance:${Date.now()}` }),
    });
    if (!res.ok) throw await this.toError(res);
    const body = (await res.json()) as { balance?: number };
    return typeof body.balance === 'number' ? body.balance : 0;
  }

  async founderStatus(): Promise<FounderStatus> {
    const res = await this.fetchResilient(`${this.base}/founder-status`, {
      method: 'GET',
      headers: { apikey: this.anonKey },
    });
    if (!res.ok) throw await this.toError(res);
    const body = (await res.json()) as Partial<FounderStatus>;
    return {
      claimed: body.claimed ?? 0,
      remaining: body.remaining ?? 0,
      total: body.total ?? 10000,
      active: body.active ?? false,
    };
  }

  private authHeaders(accessToken: string): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      apikey: this.anonKey,
    };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    return headers;
  }

  private async toError(res: Response): Promise<BackendError> {
    let code = `http_${res.status}`;
    try {
      const body = (await res.json()) as GenerateErrorBody;
      if (body.error) code = body.error;
    } catch {
      // non-JSON body; keep the http_<status> code
    }
    // The server returns 402 with error 'insufficient_credits'; normalise so the
    // controller's `err.code === 'insufficient_credits'` branch fires reliably.
    if (res.status === 402) code = 'insufficient_credits';
    return new BackendError(code, res.status);
  }
}
